import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { modulesParDefaut } from '@iobiz/shared'
import { api } from '../../api/client.js'
import {
  FormulaireBoutique,
  donneesVides,
  slugifier,
  type DonneesBoutique,
} from './FormulaireBoutique.js'

/**
 * Création d'une boutique (W-36).
 *
 * ⭐ Un seul écran découpé en blocs, pas un assistant : une boutique se crée
 * deux ou trois fois dans la vie d'un client, et un assistant y ajouterait des
 * clics sans rien apprendre.
 *
 * Le formulaire lui-même est PARTAGÉ avec l'écran de modification — c'est ce
 * qui garantit qu'un champ ajouté ici apparaisse aussi là-bas.
 */
export function NouvelleBoutiqueDialog({
  open,
  onClose,
  onCreee,
}: {
  open: boolean
  onClose: () => void
  onCreee: () => Promise<void>
}): JSX.Element {
  const [valeurs, setValeurs] = useState<DonneesBoutique>(donneesVides)
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (!open) return
    setValeurs({ ...donneesVides(), modules: modulesParDefaut('retail') })
    setErreur(null)
  }, [open])

  // Changer de métier ré-applique ses modules par défaut : c'est l'intérêt du
  // réglage, et l'utilisateur peut encore décocher ensuite.
  useEffect(() => {
    setValeurs((v) => ({ ...v, modules: modulesParDefaut(v.modele) }))
  }, [valeurs.modele])

  const pret =
    valeurs.nom.trim().length >= 2 &&
    valeurs.slug.length >= 2 &&
    (valeurs.adminEmail.trim() === '' || valeurs.adminEmail.includes('@'))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nouvelle boutique</DialogTitle>
      {/* `dividers` : c'est lui qui rend le contenu DÉFILANT. Sans lui, les
          derniers blocs sont hors de vue sans que rien ne l'indique — on croit
          alors que les champs manquent. */}
      <DialogContent dividers sx={{ pt: 3 }}>
        {erreur && <Alert severity="error" sx={{ mb: 4 }}>{erreur}</Alert>}
        <FormulaireBoutique
          valeurs={valeurs}
          mode="creation"
          onChange={(maj) => setValeurs((v) => ({ ...v, ...maj }))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          disabled={!pret || envoi}
          onClick={async () => {
            setEnvoi(true)
            setErreur(null)
            try {
              await api.post('/admin/stores', {
                name: valeurs.nom.trim(),
                // ⚠️ Forme STRICTE ici : on peut valider sans avoir quitté le
                // champ, et un separateur final serait refuse par le serveur.
                slug: slugifier(valeurs.slug),
                countryCode: valeurs.pays,
                businessModel: valeurs.modele,
                modules: valeurs.modules,
                ...(valeurs.logo ? { logoUrl: valeurs.logo } : {}),
                ...(valeurs.adresse.trim() ? { address: valeurs.adresse.trim() } : {}),
                ...(valeurs.telephone.trim() ? { phone: valeurs.telephone.trim() } : {}),
                ...(valeurs.courriel.trim() ? { email: valeurs.courriel.trim() } : {}),
                ...(valeurs.siteWeb.trim() ? { website: valeurs.siteWeb.trim() } : {}),
                ...(valeurs.raisonSociale.trim()
                  ? { legalName: valeurs.raisonSociale.trim() }
                  : {}),
                ...(valeurs.numeroFiscal.trim() ? { taxId: valeurs.numeroFiscal.trim() } : {}),
                ...(valeurs.adminEmail.trim() ? { adminEmail: valeurs.adminEmail.trim() } : {}),
                ...(valeurs.adminPrenom.trim()
                  ? { adminFirstName: valeurs.adminPrenom.trim() }
                  : {}),
                ...(valeurs.adminNom.trim() ? { adminLastName: valeurs.adminNom.trim() } : {}),
              })
              await onCreee()
            } catch (e) {
              setErreur(e instanceof Error ? e.message : 'Création impossible.')
            } finally {
              setEnvoi(false)
            }
          }}
        >
          Créer la boutique
        </Button>
      </DialogActions>
    </Dialog>
  )
}
